import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  BookOpen,
  User,
  Plus,
  Download,
  Star,
  TrendingUp,
  Library,
  Target,
  BarChart3,
  Award,
  CheckCircle,
  Zap,
  Brain,
  Activity,
  Home,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCourses, getMyEnrollments, enrollCourse } from '../../services/api';

const IntermediateEbooksLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (user) {
      fetchIntermediateEbooks();
      fetchEnrollments();
    }
  }, [user]);

  const fetchIntermediateEbooks = async () => {
    try {
      setLoading(true);
      const response = await getCourses({
        courseType: 'ebook',
        ebookSubcategory: 'intermediate',
        limit: 50
      });

      let sortedCourses = response.data.courses || [];

      // Sort courses
      switch (sortBy) {
        case 'newest':
          sortedCourses.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'popular':
          sortedCourses.sort((a, b) => (b.enrollmentCount || 0) - (a.enrollmentCount || 0));
          break;
        case 'title':
          sortedCourses.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'chapters':
          sortedCourses.sort((a, b) => (b.totalLessons || 0) - (a.totalLessons || 0));
          break;
        default:
          break;
      }

      setCourses(sortedCourses);
    } catch (error) {
      console.error('Error fetching intermediate ebooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await getMyEnrollments({ courseType: 'ebook' });
      setEnrollments(response.data.enrollments || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    try {
      await enrollCourse(courseId);
      fetchEnrollments();
    } catch (error) {
      console.error('Error enrolling in ebook:', error);
      alert('Failed to enroll in ebook');
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  const getEnrollment = (courseId) => {
    return enrollments.find((enrollment) =>
      enrollment.course?._id === courseId || enrollment.course === courseId
    );
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (user) {
        fetchIntermediateEbooks();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, sortBy]);

  const EbookCard = ({ course }) => {
    const enrolled = isEnrolled(course._id);
    const enrollment = getEnrollment(course._id);

    return (
      <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-200 hover:shadow-lg group">
        <div className="aspect-[3/4] overflow-hidden relative">
          <img
            src={course.thumbnail || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=400&h=600&q=80'}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
              <BookOpen size={24} className="text-[#30C4B5]" />
            </div>
          </div>
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-500 text-white text-xs font-medium">
              <BarChart3 size={12} className="mr-1" />
              Intermediate
            </span>
          </div>
          {course.averageRating && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center px-2 py-1 rounded bg-yellow-500 text-white text-xs font-medium">
                <Star size={12} className="mr-1" />
                {course.averageRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
              <Target size={12} className="mr-1" />
              Advanced Concepts
            </span>
            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              {course.totalLessons || 0} chapters
            </span>
          </div>

          <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
            {course.title}
          </h3>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
            {course.shortDescription}
          </p>

          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-4 space-x-4">
            <div className="flex items-center">
              <User size={12} className="mr-1" />
              {course.instructor?.firstName || 'Instructor'}
            </div>
            <div className="flex items-center">
              <BookOpen size={12} className="mr-1" />
              Digital Format
            </div>
            <div className="flex items-center">
              <Download size={12} className="mr-1" />
              PDF
            </div>
          </div>

          {enrolled ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Reading Progress
                </span>
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {enrollment?.progressPercentage || 0}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#30C4B5] rounded-full transition-all duration-300"
                  style={{ width: `${enrollment?.progressPercentage || 0}%` }}
                />
              </div>
              <button
                onClick={() => navigate(`/courses/${course._id}`)}
                className="w-full py-2 bg-[#30C4B5] hover:bg-[#29AF9F] text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center"
              >
                <BookOpen size={16} className="mr-2" />
                Continue Reading
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleEnroll(course._id)}
              className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center group"
            >
              <Plus size={16} className="mr-2 group-hover:scale-110 transition-transform" />
              Get Ebook
            </button>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#000000]">
      <main className="max-w-[1400px] mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-4">
            <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors flex items-center">
              <Home size={14} className="mr-1" />
              Dashboard
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/courses" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors">
              Courses
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/courses?courseType=ebook" className="text-gray-500 dark:text-gray-400 hover:text-[#30C4B5] transition-colors">
              Ebooks
            </Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-yellow-600 dark:text-yellow-400 font-medium">Intermediate</span>
          </nav>

          {/* Title */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-3xl text-gray-900 dark:text-white mb-1">
                Intermediate Trading Ebooks
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Advance your trading skills with sophisticated strategies, technical analysis, and market psychology concepts.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                <Library size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.length}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Intermediate Ebooks
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {enrollments.filter((e) => courses.some((c) => c._id === (e.course?._id || e.course))).length}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  In Library
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                <BookOpen size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.reduce((sum, c) => sum + (c.totalLessons || 0), 0)}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Chapters
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                <Star size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {courses.length > 0
                    ? (courses.reduce((sum, c) => sum + (c.averageRating || 0), 0) / courses.length).toFixed(1)
                    : '4.9'
                  }
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Avg Rating
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search intermediate ebooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#30C4B5] focus:border-[#30C4B5] transition-all"
            >
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
              <option value="title">Alphabetical</option>
              <option value="chapters">Most Comprehensive</option>
            </select>
          </div>
        </div>

        {/* Advanced Topics */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            Advanced Trading Concepts
          </h2>
          <div className="flex flex-wrap gap-3">
            {['Advanced Technical Analysis', 'Options Strategies', 'Algorithmic Trading', 'Market Psychology', 'Portfolio Management', 'Derivatives Trading', 'Quantitative Analysis', 'Risk Modeling'].map((topic) => (
              <span
                key={topic}
                className="px-4 py-2 bg-[#ffffff] dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:border-yellow-500 hover:text-yellow-600 dark:hover:text-yellow-400 cursor-pointer transition-all"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* Learning Path */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/10 dark:to-orange-900/10 border border-yellow-200 dark:border-yellow-900/30 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <Award size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-yellow-800 dark:text-yellow-400 mb-2">
                Intermediate Trading Mastery Path
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-500 mb-4">
                Elevate your trading expertise with advanced strategies and sophisticated market analysis techniques.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {[
                  { step: 1, title: 'Advanced Analysis', desc: 'Complex chart patterns' },
                  { step: 2, title: 'Strategy Development', desc: 'Custom trading systems' },
                  { step: 3, title: 'Risk Optimization', desc: 'Portfolio management' },
                  { step: 4, title: 'Market Psychology', desc: 'Behavioral analysis' }
                ].map((item) => (
                  <div key={item.step} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-3">
                    <div className="flex items-center mb-1">
                      <span className="w-6 h-6 bg-yellow-500 text-white text-xs font-bold rounded-full flex items-center justify-center mr-2">
                        {item.step}
                      </span>
                      <span className="font-semibold text-sm text-yellow-800 dark:text-yellow-300">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 ml-8">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Intermediate */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            Why Choose Intermediate Ebooks?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: BarChart3, title: 'Advanced Strategies', desc: 'Sophisticated trading techniques and complex market analysis methods.', color: 'bg-yellow-500' },
              { icon: Brain, title: 'Market Psychology', desc: 'Deep insights into market behavior and trader psychology.', color: 'bg-orange-500' },
              { icon: Zap, title: 'Performance Edge', desc: 'Gain competitive advantages with professional-level knowledge.', color: 'bg-purple-500' }
            ].map((benefit, index) => (
              <div key={index} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <div className={`w-12 h-12 ${benefit.color} rounded-lg flex items-center justify-center mb-4`}>
                  <benefit.icon size={24} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Prerequisites & Skills */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-200 dark:border-blue-900/30 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <Activity size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-blue-800 dark:text-blue-400 mb-2">
                Prerequisites & Skills You'll Develop
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-500 mb-4">
                Build upon your foundation with advanced concepts that require basic trading knowledge.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-300 mb-2">Prerequisites:</h4>
                  {[
                    'Basic chart reading skills',
                    'Understanding of market orders',
                    'Risk management fundamentals',
                    'Trading platform experience'
                  ].map((item, index) => (
                    <div key={index} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-2 mb-2">
                      <div className="flex items-center">
                        <CheckCircle size={14} className="text-blue-500 mr-2" />
                        <span className="text-xs text-blue-700 dark:text-blue-300">
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-300 mb-2">Skills You'll Gain:</h4>
                  {[
                    'Advanced pattern recognition',
                    'Complex strategy development',
                    'Portfolio optimization',
                    'Market sentiment analysis'
                  ].map((item, index) => (
                    <div key={index} className="bg-white/60 dark:bg-[#000000]/40 rounded-lg p-2 mb-2">
                      <div className="flex items-center">
                        <Zap size={14} className="text-orange-500 mr-2" />
                        <span className="text-xs text-blue-700 dark:text-blue-300">
                          {item}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What You'll Master */}
        <div className="mb-8">
          <h2 className="font-bold text-xl text-gray-900 dark:text-white mb-4">
            What You'll Master
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: BarChart3,
                title: 'Technical Mastery',
                topics: ['Elliott Wave Theory', 'Fibonacci Analysis', 'Volume Profile', 'Market Structure']
              },
              {
                icon: Brain,
                title: 'Psychology & Behavior',
                topics: ['Cognitive Biases', 'Market Sentiment', 'Crowd Psychology', 'Emotional Control']
              },
              {
                icon: Target,
                title: 'Strategy Development',
                topics: ['System Design', 'Backtesting', 'Optimization', 'Performance Metrics']
              },
              {
                icon: Activity,
                title: 'Risk Management',
                topics: ['Position Sizing', 'Correlation Analysis', 'Drawdown Control', 'Portfolio Theory']
              }
            ].map((category, index) => (
              <div key={index} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <category.icon size={16} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                    {category.title}
                  </h3>
                </div>
                <div className="space-y-1">
                  {category.topics.map((topic, topicIndex) => (
                    <div key={topicIndex} className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {topic}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ebooks Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
                <div className="aspect-[3/4] bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : courses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map((course) => (
              <EbookCard key={course._id} course={course} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-[#ffffff] dark:!bg-black rounded-xl border border-gray-200 dark:border-gray-800">
            <BarChart3 size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
              No intermediate ebooks found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search criteria or check back later for new intermediate ebooks.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default IntermediateEbooksLayout;
